import {CHAT_CONFIG} from "./ChatConfiguration";
import type AuthStore from "../../../authentication/AuthStore";
import testChatData from './../testing/testChatData.json';
import {environment} from "../../../env.ts";
import type {
    ADKEvent,
    ADKSessionDetail,
    AgentMetadata,
    AgentOption,
    ConsolidationToolProgress,
    SessionListResponse,
} from "../types/chatTypes";
import {normalizeADKEvent, parseSseData} from "~/pages/chat-agent/utils/adkEventMapping";
import {agentsMetadataUrl, sessionsListUrl, supersetGuestTokenUrl} from "./chatAgentApiUrls";

/** The event on one SSE line, if it is a `data:` line with a payload. */
function parseSseLine(line: string): ADKEvent | null {
    if (!line.startsWith('data: ')) return null;
    const data = line.slice(6).trim();
    return data && data !== '[DONE]' ? parseSseData(data) : null;
}

export class ChatApiStore {
    constructor(private authStore: AuthStore) {
    }

    private get headers(): Record<string, string> {
        return {
            'Authorization': `Bearer ${this.authStore.token}`,
            'Content-Type': 'application/json',
        };
    }

    async listApps(): Promise<AgentOption[]> {
        try {
            const response = await fetch(`${CHAT_CONFIG.BASE_URL}/list-apps`, {
                method: 'GET',
                headers: this.headers,
            });
            if (!response.ok) return [];
            const data = await response.json();

            // ADK returns either a string array or { apps: AppInfo[] } depending on mode
            const raw: Array<string | {name: string; description?: string}> =
                Array.isArray(data) ? data : (data.apps ?? []);

            return raw.map(item => {
                const name = typeof item === 'string' ? item : item.name;
                const description = typeof item === 'string' ? '' : (item.description ?? '');
                const label = name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
                return {appName: name, label, description};
            });
        } catch {
            return [];
        }
    }

    /** Agent chat REST API (`CHAT_CONFIG.API_URL`), not the materialized Django API. */
    async listAgentsMetadata(): Promise<AgentMetadata[]> {
        if (!CHAT_CONFIG.API_URL) {
            return [];
        }
        const response = await fetch(agentsMetadataUrl(), {headers: this.headers});
        if (!response.ok) {
            return [];
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }

    async createSession(
        bagId: number,
        appName: string,
        preferredAgent?: string,
    ): Promise<{sessionId: string; userId: string}> {
        if (environment.isTestChat) return {sessionId: 'TEST_SESSION_ID', userId: 'test_user'};

        const frontendUrl = window.location.href;
        const body: Record<string, unknown> = {
            app_name: appName,
            databag_id: bagId,
            chat_frontend_url: frontendUrl,
        };
        if (preferredAgent) {
            body.preferred_agent = preferredAgent;
        }

        const response = await fetch(`${CHAT_CONFIG.API_URL}/sessions`, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            // FastAPI puts the reason in `detail` (e.g. "Unknown agent: …"); statusText alone hides it.
            const detail = await response.json().then(b => b?.detail, () => undefined);
            const reason = typeof detail === 'string' ? `${response.statusText} (${detail})` : response.statusText;
            throw new Error(`Failed to create session: ${reason}`);
        }

        const data = await response.json();
        return {sessionId: data.session_id, userId: data.user_id};
    }

    async listSessions(
        bagId: number,
        options: {limit?: number; offset?: number} = {},
    ): Promise<SessionListResponse> {
        if (environment.isTestChat) {
            return {items: [], total: 0, limit: options.limit ?? 50, offset: options.offset ?? 0};
        }

        const limit = options.limit ?? 50;
        const offset = options.offset ?? 0;
        const response = await fetch(sessionsListUrl(bagId, limit, offset), {
            method: 'GET',
            headers: this.headers,
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch session list: ${response.statusText}`);
        }

        return await response.json();
    }

    async getSessionDetail(sessionId: string, appName: string): Promise<ADKSessionDetail> {
        if (environment.isTestChat) {
            return {events: testChatData.map(normalizeADKEvent)};
        }

        const response = await fetch(
            `${CHAT_CONFIG.API_URL}/sessions/${sessionId}?app_name=${encodeURIComponent(appName)}`,
            {
                method: 'GET',
                headers: this.headers,
            },
        );

        if (!response.ok) {
            throw new Error(`Failed to fetch session detail: ${response.statusText}`);
        }

        const detail = await response.json();
        return {
            userId: detail.userId ?? detail.user_id,
            events: (detail.events ?? []).map(normalizeADKEvent),
        };
    }

    /**
     * Poll in-process tail-spend consolidation progress (written while consolidate_* tools run).
     * @param appName ADK app the session belongs to (must match create_session / run_sse).
     */
    async getTailSpendConsolidationProgress(
        sessionId: string,
        appName: string,
    ): Promise<ConsolidationToolProgress | null> {
        if (environment.isTestChat || !CHAT_CONFIG.API_URL) {
            return null;
        }
        try {
            const q = new URLSearchParams({
                session_id: sessionId,
                app_name: appName,
            });
            const response = await fetch(
                `${CHAT_CONFIG.API_URL}/tail-spend-consolidation-progress?${q.toString()}`,
                {method: 'GET', headers: this.headers},
            );
            if (!response.ok) {
                return null;
            }
            const data: unknown = await response.json();
            const progress = (data as {progress?: unknown}).progress;
            if (progress == null || typeof progress !== 'object') {
                return null;
            }
            const o = progress as Record<string, unknown>;
            const num = (v: unknown): number | undefined =>
                typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
            return {
                stage: String(o.stage ?? ''),
                completed: Number(o.completed) || 0,
                total: Math.max(Number(o.total) || 1, 1),
                detail: typeof o.detail === 'string' ? o.detail : undefined,
                stageNumber: num(o.stage_number) ?? num((o as {stageNumber?: unknown}).stageNumber),
                stageTotal: num(o.stage_total) ?? num((o as {stageTotal?: unknown}).stageTotal) ?? 4,
            };
        } catch {
            return null;
        }
    }

    /**
     * POSTs a `new_message` to ADK's /run_sse endpoint and yields the parsed event stream. Aborting
     * `signal` closes the connection, which cancels the run on the server; the iteration then throws
     * an `AbortError`.
     */
    private async *streamRunSse(
        sessionId: string,
        userId: string,
        appName: string,
        newMessage: {role: string; parts: Array<Record<string, unknown>>},
        signal?: AbortSignal,
    ): AsyncGenerator<ADKEvent> {
        const response = await fetch(`${CHAT_CONFIG.BASE_URL}/run_sse`, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                app_name: appName,
                user_id: userId,
                session_id: sessionId,
                new_message: newMessage,
                streaming: true,
            }),
            signal,
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(`SSE request failed (${response.status}): ${text}`);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const {done, value} = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, {stream: true});
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                const event = parseSseLine(line);
                if (event) yield event;
            }
        }

        // Process any remaining buffer
        const event = parseSseLine(buffer);
        if (event) yield event;
    }

    /**
     * Send a message via ADK's /run_sse endpoint and return an async iterator of ADK events.
     */
    sendMessageSSE(
        sessionId: string,
        userId: string,
        content: string,
        appName: string,
        signal?: AbortSignal,
    ): AsyncGenerator<ADKEvent> {
        return this.streamRunSse(sessionId, userId, appName, {role: 'user', parts: [{text: content}]}, signal);
    }

    /**
     * Resumes a paused long-running tool call (e.g. `adk_request_input`) by sending back a
     * `function_response` part whose `id`/`name` match the originating `functionCall`.
     */
    sendFunctionResponseSSE(
        sessionId: string,
        userId: string,
        appName: string,
        functionResponse: {id: string; name: string; response: unknown},
        signal?: AbortSignal,
    ): AsyncGenerator<ADKEvent> {
        return this.streamRunSse(sessionId, userId, appName, {
            role: 'user',
            parts: [{function_response: functionResponse}],
        }, signal);
    }

    /**
     * Mints a fresh Superset guest token for an embedded dashboard.
     * Tokens are short-lived (~5 min); @superset-ui/embedded-sdk calls this
     * repeatedly via its fetchGuestToken callback.
     */
    async getSupersetGuestToken(dashboardUuid: string): Promise<string> {
        const response = await fetch(supersetGuestTokenUrl(dashboardUuid), {
            method: 'GET',
            headers: this.headers,
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch Superset guest token: ${response.statusText}`);
        }

        const data = await response.json();
        return data.token;
    }

    async getDownloadUrl(filename: string): Promise<string> {
        const response = await fetch(`${CHAT_CONFIG.API_URL}/download/token/${filename}`, {
            method: 'GET',
            headers: this.headers,
        });

        if (!response.ok) {
            throw new Error(`Failed to find the file: ${response.statusText}`);
        }

        const data = await response.json();
        return data.download_url;
    }

    async saveFeedback(
        sessionId: string,
        eventId: string,
        invocationId: string,
        isHelpful: boolean,
        category: string | null = null,
        description: string | null = null
    ): Promise<void> {
        if (environment.isTestChat) {
            console.log('Test mode: Feedback would be saved', {
                sessionId,
                eventId,
                invocationId,
                isHelpful,
                category,
                description,
            });
            return;
        }

        const response = await fetch(`${CHAT_CONFIG.API_URL}/feedback`, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                session_id: sessionId,
                event_id: eventId,
                invocation_id: invocationId,
                is_helpful: isHelpful,
                category,
                description,
            }),
        });

        if (!response.ok) {
            throw new Error(`Failed to save feedback: ${response.statusText}`);
        }
    }
}
